import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { STR } from "@/i18n";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("oj_lang") || "en");
  const [content, setContent] = useState({});
  const [overrides, setOverrides] = useState({});
  const [pages, setPages] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const t = STR[lang];

  const toggleLang = useCallback(() => {
    const next = lang === "en" ? "hi" : "en";
    setLang(next);
    localStorage.setItem("oj_lang", next);
  }, [lang]);

  useEffect(() => {
    document.body.classList.toggle("lang-hi", lang === "hi");
  }, [lang]);

  const loadContent = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data);
    } catch (e) {
      console.error("Failed to load content", e);
    }
  }, []);

  const loadOverrides = useCallback(async () => {
    try {
      const { data } = await api.get("/overrides");
      setOverrides(data);
    } catch (e) {
      console.error("Failed to load overrides", e);
    }
  }, []);

  const loadPages = useCallback(async () => {
    try {
      const { data } = await api.get("/pages");
      setPages(data);
    } catch (e) {
      console.error("Failed to load pages", e);
    }
  }, []);

  useEffect(() => { loadContent(); loadOverrides(); loadPages(); }, [loadContent, loadOverrides, loadPages]);

  const saveText = useCallback(async (key, value) => {
    const { data } = await api.put(`/overrides/${key}`, { value });
    setOverrides((o) => ({ ...o, [key]: { value: data.value, type: "text" } }));
  }, []);

  const saveImage = useCallback(async (key, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/overrides/${key}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setOverrides((o) => ({ ...o, [key]: { value: data.value, type: "image" } }));
  }, []);

  const resetOverride = useCallback(async (key) => {
    await api.delete(`/overrides/${key}`);
    setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });
  }, []);

  const ov = useCallback((key) => overrides[key]?.value, [overrides]);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setAuthReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); }
    setUser(null);
    setEditMode(false);
  }, []);

  const value = useMemo(() => ({
    lang, t, toggleLang, content, loadContent, user, setUser, authReady, login, logout,
    overrides, ov, loadOverrides, editMode, setEditMode, saveText, saveImage, resetOverride,
    pages, loadPages,
  }), [lang, t, toggleLang, content, loadContent, user, authReady, login, logout, overrides, ov, loadOverrides, editMode, saveText, saveImage, resetOverride, pages, loadPages]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
